import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, ArrowRight, Users } from 'lucide-react';
import api from '@/lib/api';
import { getToken } from '@/lib/tokenStore';
import { playVoiceOver } from '@/lib/voiceOver';
import VideoRoom from '@/components/VideoRoom';

const FREE_GROUP_ID = '0ad0f063-9b6c-4c70-a63d-337fab466af1';
const POPUP_TIMEOUT_MS = 60000; // was 10000 — updated to 60s per spec
const GUEST_ID_KEY = 'sam247_guest_id';
const CAP_MESSAGE = 'Der er i øjeblikket mange, som deltager i åbne samtaler. Vi kan derfor ikke lade flere deltage uden at kvaliteten af samtalerne falder. Prøv derfor igen senere – Undskyld ulejligheden';

// Any popup being open triggers hard mute on the VideoRoom
export default function FreeGroupJoinedPage() {
  const navigate = useNavigate();
  const [fingerprint, setFingerprint] = useState('');
  const [record, setRecord] = useState(null);  // FREEUSERSINFO doc after P002
  const [remaining, setRemaining] = useState(null);
  // NEW FLOW: mount VideoRoom immediately on landing — welcome voice plays right away.
  // `validated` flips to true once P010 accepts the guest → mic/speaker stays open,
  // popup timers stop firing, ticker starts.
  const [joined] = useState(true);
  const [validated, setValidated] = useState(false);
  const farewellPlayingRef = useRef(false);

  // Supervisor + cap state
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [supervisorLoaded, setSupervisorLoaded] = useState(false);
  const [connectedCount, setConnectedCount] = useState(null);
  const [capacity, setCapacity] = useState(6);
  const [denied, setDenied] = useState(false); // true → show cap-exceeded info-box, don't mount VideoRoom

  // Guest ID in localStorage (P001 addition)
  const [guestId] = useState(() => {
    try {
      let g = localStorage.getItem(GUEST_ID_KEY);
      if (!g) {
        g = (crypto.randomUUID && crypto.randomUUID()) || `guest_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(GUEST_ID_KEY, g);
      }
      return g;
    } catch {
      return `guest_${Date.now()}`;
    }
  });

  // Popup 1 state
  const [popup1Open, setPopup1Open] = useState(false);
  const [choices, setChoices] = useState({ email: false, device: false, none: false });
  const popup1TimerRef = useRef(null);
  const popup1ShowCountRef = useRef(0);
  const pendingPermsRef = useRef({ allowEmail: false, allowDevice: false });

  // Popup 2 state
  const [popup2Open, setPopup2Open] = useState(false);
  const [popup2Email, setPopup2Email] = useState('');
  const popup2TimerRef = useRef(null);

  // Popup 3 state
  const [popup3Open, setPopup3Open] = useState(false);
  const popup3TimerRef = useRef(null);

  const tickTimerRef = useRef(null);
  // Note: welcome/farewell voice-over is played automatically by VideoRoom
  // (see VOICE_WELCOME_URL / VOICE_FAREWELL_URL in components/VideoRoom.jsx).

  // Any popup open? → hard-mute VideoRoom (mic + speaker) while popup is visible
  const anyPopupOpen = popup1Open || popup2Open || popup3Open;

  // Fingerprint on mount
  useEffect(() => {
    (async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setFingerprint(result.visitorId);
      } catch { setFingerprint(''); }
    })();
  }, []);

  // Detect whether the current session belongs to a supervisor. Supervisors:
  //   • bypass the 6-user cap
  //   • see a live connected-users counter on the free-group page
  //   • are NOT shown the free-user popup flow (already logged in)
  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        if (!token) { setSupervisorLoaded(true); return; }
        const { data } = await api.get('/auth/me');
        setIsSupervisor(!!data?.user?.this_is_supervisor);
        setIsAdmin(!!data?.user?.is_admin);
      } catch { /* not logged in — treat as guest */ }
      finally { setSupervisorLoaded(true); }
    })();
  }, []);

  // Pre-flight cap check + live counter poll. Runs every 5s.
  useEffect(() => {
    if (!supervisorLoaded) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get('/free-group/status');
        if (stopped) return;
        setConnectedCount(data.connected);
        setCapacity(data.capacity);
        // Only deny non-supervisors when the cap is already reached at page-load.
        // Once validated=true (already in the room), don't kick them out mid-session.
        if (!isSupervisor && !validated && data.connected >= data.capacity) {
          setDenied(true);
        }
      } catch { /* transient network hiccup — keep polling */ }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorLoaded, isSupervisor]);

  // 300-second delay before first popup — but only if guest hasn't already validated
  // AND is not a supervisor (supervisors bypass the popup flow entirely).
  useEffect(() => {
    if (!supervisorLoaded) return;   // wait until we know if user is a supervisor
    if (isSupervisor) return;         // supervisors skip popups
    if (validated) return;            // no popups if already validated
    if (denied) return;               // no popups if we're already showing the cap info-box
    const t = setTimeout(() => {
      if (validated || isSupervisor || denied) return;
      popup1ShowCountRef.current = 1;
      showPopup1();
    }, 300000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validated, isSupervisor, supervisorLoaded, denied]);

  const clearAllTimers = () => {
    if (popup1TimerRef.current) clearTimeout(popup1TimerRef.current);
    if (popup2TimerRef.current) clearTimeout(popup2TimerRef.current);
    if (popup3TimerRef.current) clearTimeout(popup3TimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
  };
  useEffect(() => () => clearAllTimers(), []);

  const FAREWELL_URL = 'https://customer-assets-lqy194kg.emergentagent.net/job_unified-video-chat/artifacts/f9t6ob0h_davinci_tak_for_at_du_deltog_i_samtalen_i_gruppen___du_er_.mp3';

  const terminateSession = () => {
    clearAllTimers();
    if (farewellPlayingRef.current) return; // already terminating
    farewellPlayingRef.current = true;
    const finish = () => {
      toast.info('Din gratis session er afsluttet');
      navigate('/login');
    };
    // Route through the singleton so welcome + farewell never overlap. If
    // something is already playing, this call resolves immediately.
    const audio = playVoiceOver(FAREWELL_URL, { volume: 0.95, onEnd: finish });
    if (!audio) finish();
  };

  // --- P010_Validate free user before releasing minutes ---
  // Uses email and/or fingerprint + IP + device name — with permission flags from Popup 1.
  const runP010 = async ({ email = '', fp = '', allowEmail = false, allowDevice = false } = {}) => {
    try {
      const deviceName = allowDevice ? (navigator.userAgent || '').slice(0, 255) : '';
      const payload = {
        email: allowEmail ? email : '',
        fingerprint: allowDevice ? fp : '',
        device_name: deviceName,
        allow_email: !!allowEmail,
        allow_device: !!allowDevice,
      };
      const { data: lookup } = await api.post('/free/lookup', payload);
      if (lookup.status === 'new') {
        // P001 — create + P002
        const { data: created } = await api.post('/free/register-usage', payload);
        setRecord({ id: created.record_id, email });
        setRemaining(created.remain_free_minutes);
        setValidated(true);
        startTicker(created.record_id);
        popup1ShowCountRef.current = 0;
      } else if (lookup.status === 'accepted') {
        setRecord({ id: lookup.record_id, email });
        setRemaining(lookup.remain_free_minutes);
        setValidated(true);
        startTicker(lookup.record_id);
        popup1ShowCountRef.current = 0;
      } else {
        // exhausted → åbn registreringssiden
        toast.info('Din gratis taletid er brugt op — opret venligst en konto.');
        navigate('/login?tab=register');
      }
    } catch (err) {
      toast.error('Kunne ikke validere din information');
      showPopup3();
    }
  };

  // --- Popup 1 ---
  const showPopup1 = () => {
    setChoices({ email: false, device: false, none: false });
    setPopup1Open(true);
    popup1TimerRef.current = setTimeout(() => resolvePopup1(true), POPUP_TIMEOUT_MS);
  };
  const resolvePopup1 = (fromTimeout = false) => {
    if (popup1TimerRef.current) clearTimeout(popup1TimerRef.current);
    setPopup1Open(false);
    const { email, device, none } = choices;
    // Rule (updated): if ONLY item 2 (device) is marked → skip Popup 2, call P010 directly.
    if (device && !email && !none) {
      runP010({ email: '', fp: fingerprint, allowEmail: false, allowDevice: true });
      return;
    }
    if ((email || device) && !none) {
      // A or B (with email option) → Popup 2 to collect email
      setPopup2Email('');
      // Remember the checked options for the eventual runP010 call after Popup 2
      pendingPermsRef.current = { allowEmail: !!email, allowDevice: !!device };
      setPopup2Open(true);
      popup2TimerRef.current = setTimeout(() => { setPopup2Open(false); showPopup3(); }, POPUP_TIMEOUT_MS);
    } else {
      // C or D (none or nothing marked) → Popup 3
      showPopup3();
    }
  };

  // --- Popup 3 ---
  const showPopup3 = () => {
    setPopup3Open(true);
    popup3TimerRef.current = setTimeout(() => { setPopup3Open(false); terminateSession(); }, POPUP_TIMEOUT_MS);
  };
  const popup3Yes = () => { setPopup3Open(false); terminateSession(); };
  const popup3No = () => {
    setPopup3Open(false);
    if (popup3TimerRef.current) clearTimeout(popup3TimerRef.current);
    if (popup1ShowCountRef.current >= 2) {
      terminateSession();
      return;
    }
    popup1ShowCountRef.current += 1;
    showPopup1();
  };

  // --- Popup 2 OK ---
  const popup2Ok = async () => {
    if (popup2TimerRef.current) clearTimeout(popup2TimerRef.current);
    setPopup2Open(false);
    const email = popup2Email.trim().toLowerCase();
    const { allowEmail, allowDevice } = pendingPermsRef.current;
    await runP010({ email, fp: fingerprint, allowEmail, allowDevice });
  };

  // --- Ticker: decrement minutes every 60 sec via backend ---
  const startTicker = (recordId) => {
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    tickTimerRef.current = setInterval(async () => {
      try {
        const { data } = await api.post('/free/tick', { record_id: recordId, minutes: 1 });
        setRemaining(data.remain_free_minutes);
        if (data.remain_free_minutes <= 0) {
          clearInterval(tickTimerRef.current);
          toast.info('Din gratis taletid er brugt op');
          navigate('/login?tab=register');
        }
      } catch { /* keep counting */ }
    }, 60000);
  };

  // "købe taletid nu" click
  const [buyConfirmOpen, setBuyConfirmOpen] = useState(false);
  const confirmBuyGoToRegister = () => {
    setBuyConfirmOpen(false);
    clearAllTimers();
    navigate('/login?tab=register');
  };

  const showLowInfo = record && remaining !== null && remaining < 15;

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="free-group-joined-page">
      {/* LEFT */}
      <section className="flex flex-col justify-center px-8 py-16 md:px-16 lg:px-24 bg-[#09090B]">
        <div className="max-w-lg space-y-6 animate-fade-up">
          <div className="text-xs uppercase tracking-widest text-volt">samtale24-7.dk</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Welcome to <span className="text-volt">Samtale24-7.dk</span>
          </h1>
          <p className="font-display text-2xl sm:text-3xl font-semibold text-white/90">
            Du er nu tilsluttet en samtalegruppe
          </p>

          {record && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-volt/40 bg-volt/10 px-4 py-3 text-sm font-medium text-volt"
              data-testid="free-remaining-pill"
            >
              Resterende gratis taletid: <span className="font-bold">{remaining ?? '—'}</span> minutter
            </div>
          )}

          {/* Supervisor / Admin-only counter now lives inside VideoRoom (below the mic/leave pill). */}

          {/* Cap-exceeded info-box — replaces the whole VideoRoom */}
          {denied && (
            <div
              className="mt-4 rounded-md border border-yellow-500/50 bg-yellow-500/10 px-5 py-4 text-sm text-yellow-100"
              data-testid="cap-exceeded-infobox"
            >
              <p className="font-semibold mb-2 text-yellow-200">Prøv igen senere</p>
              <p>{CAP_MESSAGE}</p>
            </div>
          )}

          <div className="pt-6">
            <Button asChild variant="outline" className="border-border hover:bg-[#18181B] hover:text-white" data-testid="free-group-back-btn">
              <Link to="/login"><ArrowLeft className="w-4 h-4 mr-2" /> Log ind</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* RIGHT */}
      <section
        className="bg-[#0f0f10] border-l border-border flex items-center justify-center min-h-[300px] md:min-h-full px-8 md:px-14 py-12"
        data-testid="free-group-joined-right"
      >
        {showLowInfo ? (
          <div className="max-w-md space-y-5 animate-fade-up" data-testid="free-low-info-block">
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Opnå alle fordelene ved at købe taletid</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Tal anonymt eller med video – efter eget valg</li>
              <li>• Få alle fordelene ved at lade sig registrere</li>
              <li>• Benyt dit særlige introtilbud ved første køb</li>
              <li>• Ingen automatiske gentegninger – du skal selv købe når du vil</li>
              <li>• Køb 3 timer eller fri taletid i 1 måned</li>
            </ul>
            <Button
              onClick={() => setBuyConfirmOpen(true)}
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
              data-testid="buy-talk-time-btn"
            >
              Købe taletid nu <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : null}
      </section>

      {/* WEBRTC OVERLAY — mounted immediately on landing (welcome voice plays here).
          Suppressed when the free group is at capacity (unless the user is a supervisor).
          skipFarewell=true because terminateSession handles the farewell audio for guests. */}
      {joined && !denied && (
        <VideoRoom
          groupId={FREE_GROUP_ID}
          groupName="Gratis samtale"
          displayName="Gæst"
          myNickname="Gæst"
          withVideo={false}
          guestId={guestId}
          forceMuted={anyPopupOpen}
          skipFarewell={true}
          showConnectedCount={isSupervisor || isAdmin}
          hideParticipantInfo={true}
          showConnectionStatus={true}
          onLeave={terminateSession}
        />
      )}

      {/* POPUP 1 */}
      <Dialog open={popup1Open} onOpenChange={() => {}}>
        <DialogContent className="bg-[#18181B] border-border" data-testid="popup1">
          <DialogHeader>
            <DialogTitle>Gratis taletid</DialogTitle>
            <DialogDescription>
              For at give dig 30 minutters gratis taletid har vi brug for enten din email og/eller enhedsinformation. Vælg hvad du foretrækker:
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-volt italic -mt-2" data-testid="popup-audio-notice">
            Vi har deaktiveret din lyd i gruppen, mens du vælger den løsning som er bedst for dig.
          </p>
          <div className="space-y-3 py-2">
            {[
              { key: 'email', label: 'Min email-adresse' },
              { key: 'device', label: 'Mit enheds-id' },
              { key: 'none', label: 'Ingen af ovenstående' },
            ].map((o) => (
              <label key={o.key} className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={choices[o.key]}
                  onCheckedChange={(v) => setChoices((c) => ({ ...c, [o.key]: !!v }))}
                  data-testid={`popup1-choice-${o.key}`}
                />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => resolvePopup1(false)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="popup1-ok-btn">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POPUP 2 */}
      <Dialog open={popup2Open} onOpenChange={() => {}}>
        <DialogContent className="bg-[#18181B] border-border" data-testid="popup2">
          <DialogHeader>
            <DialogTitle>Din email-adresse</DialogTitle>
            <DialogDescription>Angiv venligst din email-adresse for at få 30 minutters gratis taletid.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-volt italic -mt-2">
            Vi har deaktiveret din lyd i gruppen, mens du vælger den løsning som er bedst for dig.
          </p>
          <div className="space-y-3 py-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={popup2Email}
              onChange={(e) => setPopup2Email(e.target.value)}
              className="bg-[#09090B] border-border"
              placeholder="dig@eksempel.dk"
              data-testid="popup2-email-input"
            />
          </div>
          <DialogFooter>
            <Button onClick={popup2Ok} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="popup2-ok-btn">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POPUP 3 */}
      <Dialog open={popup3Open} onOpenChange={() => {}}>
        <DialogContent className="bg-[#18181B] border-border" data-testid="popup3">
          <DialogHeader>
            <DialogTitle>Er du sikker?</DialogTitle>
            <DialogDescription>
              Er du sikker på dit valg? I så fald må vi afslutte din gratis session.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-volt italic -mt-2">
            Vi har deaktiveret din lyd i gruppen, mens du vælger den løsning som er bedst for dig.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={popup3No} className="border-border" data-testid="popup3-no-btn">Nej</Button>
            <Button onClick={popup3Yes} className="bg-red-500 text-white hover:bg-red-600 font-semibold" data-testid="popup3-yes-btn">Ja, afslut session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BUY CONFIRM */}
      <Dialog open={buyConfirmOpen} onOpenChange={setBuyConfirmOpen}>
        <DialogContent className="bg-[#18181B] border-border" data-testid="buy-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Fortsæt til registrering?</DialogTitle>
            <DialogDescription>
              Din nuværende gratis session vil blive afbrudt. Vil du fortsætte til registreringen?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuyConfirmOpen(false)} className="border-border">Nej, bliv her</Button>
            <Button onClick={confirmBuyGoToRegister} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="buy-confirm-yes">Ja, fortsæt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
